import { Button, ButtonGroup, ErrorPage } from '@uhg-uicl/ui-core/dist';
import LoadingPage from 'components/LoadingPage';
import { BaseRoute } from 'constants/routes';
import { useAuth } from 'react-oidc-context';
import { setUser } from 'containers/PageWrapper/modules/actions';
import { connect } from 'react-redux';
import { UserProfile } from '../modules/types';
import { useEffect, useState } from 'react';
import {
  AcknowledgedStatus,
  AuthenticationStatus,
  LocalStorageKeys,
  authenticationMessages,
  guardianLink,
} from '../modules/constants';
import { push } from 'connected-react-router';
import { getLoggedInUser } from '../modules/utils';

type AuthModalProps = {
  dispatchSetUser: Function;
  dispatchPush: Function;
};

const LoginFooter = () => {
  return (
    <>
      {/* @ts-ignore */}
      <ButtonGroup className="footer">
        <Button
          buttonType="emphasized"
          name="Login"
          onClick={() => {
            localStorage.setItem(
              LocalStorageKeys.user_acknowledged,
              AcknowledgedStatus.Acknowledged,
            );
            window.location.href = `${BaseRoute}/login?location=fromLogout`;
          }}
          size="medium"
          type="button"
          dataTestId="go-back-home-btn"
        />
      </ButtonGroup>
    </>
  );
};

enum AuthState {
  LoggedOut,
  Loading,
  Error,
  Authenticated,
  NotAuthenticated,
}

const AuthModal = ({ dispatchSetUser, dispatchPush }: AuthModalProps) => {
  const auth = useAuth();
  const [authenticationState, setAuthenticationState] = useState(AuthState.LoggedOut);

  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.user_access_token, '');
    localStorage.setItem(
      LocalStorageKeys.user_authenticated,
      AuthenticationStatus.NotAuthenticated,
    );
    if (!auth.isLoading && !auth.isAuthenticated) {
      setAuthenticationState(AuthState.LoggedOut);
      return;
    }
    if (!auth.isAuthenticated) {
      return;
    }

    localStorage.setItem(LocalStorageKeys.user_access_token, auth.user?.access_token ?? '');
    const authCall = async () => {
      setAuthenticationState(AuthState.Loading);
      // Users have to have at least one permission to be authenticated
      const guardianPermissions: any = auth?.user?.profile?.guardianPermissions;
      let authenticated = false;
      if (guardianPermissions) {
        guardianPermissions?.userAccounts?.forEach((userAccount: any) => {
          let userClientPermissions: string[] = [];
          if (userAccount.organization['clientPartners']) {
            userAccount.organization['clientPartners'].forEach((clientPartners) => {
              userClientPermissions = userClientPermissions.concat(clientPartners.permissions);
            });
          }
          userClientPermissions.forEach((permissionRole: any) => {
            const permissionRoleArray = permissionRole.split(':');
            if (permissionRoleArray.length > 1 && permissionRoleArray[0] === guardianLink) {
              authenticated = true;
            }
          });
        });
      }
      setAuthenticationState(authenticated ? AuthState.Authenticated : AuthState.NotAuthenticated);
    };
    authCall();
  }, [auth.isAuthenticated]);

  // Check to see if user is silently refreshing
  if (window.location.href.includes('silent-refresh')) {
    auth.startSilentRenew();
    auth
      .signinSilent()
      .then((response) => {
        if (response) {
          window.location.href = BaseRoute;
        } else {
          // signed out
          window.location.href = `${BaseRoute}/logout`;
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
    auth.stopSilentRenew();
    return (
      <LoadingPage
        className="auth-loading"
        dataTestId="auth-loading-id"
        domID="auth-loading-id"
        length="50px"
        message={authenticationMessages.sessionUpdating}
      />
    );
  }
  localStorage.setItem(LocalStorageKeys.user_authenticated, AuthenticationStatus.NotAuthenticated);

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return (
        <LoadingPage
          className="auth-loading"
          dataTestId="auth-loading-id"
          domID="auth-loading-id"
          length="50px"
          message={authenticationMessages.signingIn}
        />
      );
    case 'signoutRedirect':
      setAuthenticationState(AuthState.LoggedOut);
      return (
        <LoadingPage
          className="auth-loading"
          dataTestId="auth-loading-id"
          domID="auth-loading-id"
          length="50px"
          message={authenticationMessages.signingOut}
        />
      );
  }

  if (auth.isLoading) {
    return (
      <LoadingPage
        className="auth-loading"
        dataTestId="auth-loading-id"
        domID="auth-loading-id"
        length="50px"
        message={authenticationMessages.validating}
      />
    );
  }

  if (auth.error) {
    return (
      <ErrorPage
        title="Login Error"
        message={`${authenticationMessages.error} ${auth.error.message}`}
      />
    );
  }

  // Set up user object
  const loggedInUser: UserProfile = getLoggedInUser(auth);

  switch (authenticationState) {
    case AuthState.Loading:
      return (
        <LoadingPage
          className="auth-loading"
          dataTestId="auth-loading-id"
          domID="auth-loading-id"
          length="50px"
          message={authenticationMessages.validating}
        />
      );
    case AuthState.Error:
      return (
        <ErrorPage title="Authentication Error" message={authenticationMessages.notAuthorized} />
      );
    case AuthState.NotAuthenticated:
      return <ErrorPage title="Not Authorized" message={authenticationMessages.notAuthorized} />;
    case AuthState.Authenticated:
      dispatchSetUser(loggedInUser);
      localStorage.setItem(LocalStorageKeys.user_access_token, auth.user?.access_token ?? '');
      localStorage.setItem(LocalStorageKeys.user_authenticated, AuthenticationStatus.Authenticated);
      dispatchPush(BaseRoute);
      return (
        <LoadingPage
          className="auth-loading"
          dataTestId="auth-loading-id"
          domID="auth-loading-id"
          length="50px"
          message={authenticationMessages.redirecting}
        />
      );
    default:
      return (
        <ErrorPage
          title="Logged Out"
          message={authenticationMessages.loggedOut}
          footer={LoginFooter}
          dataTestId="logged-out-card"
        />
      );
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSetUser: (user: any) => dispatch(setUser(user)),
  dispatchPush: (location) => dispatch(push(location)),
});

const mapStateToProps = (state: any) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AuthModal);
